/**
 * Buscador de tareas para elegir una (la madre de una subtarea, una subtarea existente o un
 * bloqueo). Combobox accesible hecho a mano (patrón ARIA «combobox con lista»): el campo tiene
 * `role="combobox"` y la lista `role="listbox"`; ↑/↓ recorren las opciones habilitadas
 * (`aria-activedescendant`), Enter elige y Esc cierra el buscador sin cerrar la ficha.
 *
 * Busca con la API (TanStack Query, `useBuscarTareas`): «CDT-12», «12» o parte del título, abiertas y
 * cerradas, con el mismo retardo que «Tareas parecidas». Quien lo usa decide qué tareas no se pueden elegir
 * y por qué (`motivoDeshabilitada`): se muestran atenuadas con el motivo, al final de la lista.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { formatearNumero, type Tarea } from '@cdtec-backlog/contrato';
import { Giro } from '../../componentes/Esqueleto';
import { Icono } from '../../componentes/Icono';
import { useBuscarTareas } from './hooks';
import { EstadoTarea } from './MarcaCierre';
import { RETARDO_PARECIDAS_MS } from './TareasParecidas';
import './BuscadorTarea.css';

interface Props {
  /** Nombre accesible del campo («Buscar la tarea madre»). */
  etiqueta: string;
  placeholder?: string;
  /** La tarea de la ficha: aparece deshabilitada con «Es esta tarea». */
  tareaActualId: number;
  /** Motivo por el que una candidata no se puede elegir, o `null` si se puede. */
  motivoDeshabilitada?: (candidata: Tarea) => string | null;
  /** Mientras se guarda la elección: el campo sigue visible pero no se elige otra. */
  ocupado?: boolean;
  onElegir: (tarea: Tarea) => void;
  onCerrar: () => void;
}

interface Opcion {
  tarea: Tarea;
  motivo: string | null;
}

export function BuscadorTarea({
  etiqueta,
  placeholder = 'CDT-12, 12 o parte del título',
  tareaActualId,
  motivoDeshabilitada,
  ocupado = false,
  onElegir,
  onCerrar,
}: Props) {
  const id = useId();
  const campo = useRef<HTMLInputElement>(null);
  const [texto, setTexto] = useState('');
  const [buscado, setBuscado] = useState('');
  const [activa, setActiva] = useState<number | null>(null);

  useEffect(() => {
    campo.current?.focus();
  }, []);

  // Mientras guarda, el campo queda de solo lectura (no `disabled`: perdería el foco). Si la API falla y el
  // buscador sigue abierto, el foco vuelve al campo para poder elegir otra.
  const estabaOcupado = useRef(ocupado);
  useEffect(() => {
    if (estabaOcupado.current && !ocupado) campo.current?.focus();
    estabaOcupado.current = ocupado;
  }, [ocupado]);

  useEffect(() => {
    const espera = setTimeout(() => setBuscado(texto), RETARDO_PARECIDAS_MS);
    return () => clearTimeout(espera);
  }, [texto]);

  const resultados = useBuscarTareas(buscado);
  const conTexto = buscado.trim().length > 0;
  // Las que se pueden elegir primero; dentro de cada grupo, el orden de la API.
  const opciones: Opcion[] = conTexto
    ? (resultados.data ?? [])
        .map((tarea) => ({
          tarea,
          motivo:
            tarea.id === tareaActualId ? 'Es esta tarea' : (motivoDeshabilitada?.(tarea) ?? null),
        }))
        .sort((a, b) => Number(a.motivo !== null) - Number(b.motivo !== null))
    : [];
  const habilitadas = opciones.filter((o) => o.motivo === null).map((o) => o.tarea.id);
  // Si la activa ya no está (otra búsqueda), se pierde la marca.
  const activaVigente = activa !== null && habilitadas.includes(activa) ? activa : null;
  const idOpcion = (tareaId: number) => `${id}-opcion-${tareaId}`;
  // Expandido solo si hay opciones que mostrar.
  const abierta = opciones.length > 0;

  const elegir = (opcion: Opcion) => {
    if (opcion.motivo !== null || ocupado) return;
    onElegir(opcion.tarea);
  };

  const alTeclear = (evento: KeyboardEvent<HTMLInputElement>) => {
    if (evento.key === 'Escape') {
      // Solo el buscador: la ficha ignora los Esc ya atendidos.
      evento.preventDefault();
      onCerrar();
      return;
    }
    if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
      evento.preventDefault();
      if (habilitadas.length === 0) return;
      const total = habilitadas.length;
      const actual = activaVigente === null ? -1 : habilitadas.indexOf(activaVigente);
      const abajo = evento.key === 'ArrowDown';
      // Sin activa: ↓ va a la primera y ↑ a la última; con activa, da la vuelta en los extremos.
      const indice =
        actual === -1 ? (abajo ? 0 : total - 1) : (actual + (abajo ? 1 : -1) + total) % total;
      const nueva = habilitadas[indice];
      setActiva(nueva ?? null);
      if (nueva !== undefined)
        document.getElementById(idOpcion(nueva))?.scrollIntoView?.({ block: 'nearest' });
      return;
    }
    if (evento.key === 'Enter') {
      // Enter nunca envía un formulario de alrededor.
      evento.preventDefault();
      const opcion = opciones.find((o) => o.tarea.id === activaVigente);
      if (opcion) elegir(opcion);
    }
  };

  const estadoBusqueda = !conTexto
    ? 'Escribe el número (CDT-12 o 12) o parte del título.'
    : resultados.isPending
      ? 'Buscando…'
      : resultados.isError
        ? 'No se pudo buscar. Revisa tu conexión.'
        : opciones.length === 0
          ? 'Sin resultados.'
          : `${opciones.length} ${opciones.length === 1 ? 'resultado' : 'resultados'}.`;

  return (
    <div className="buscador-tarea">
      <div className="buscador-tarea__campo">
        <Icono nombre="lupa" />
        <input
          ref={campo}
          type="text"
          role="combobox"
          aria-label={etiqueta}
          aria-expanded={abierta}
          aria-controls={`${id}-lista`}
          aria-autocomplete="list"
          aria-activedescendant={activaVigente === null ? undefined : idOpcion(activaVigente)}
          aria-describedby={`${id}-estado`}
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          value={texto}
          readOnly={ocupado}
          aria-busy={ocupado || undefined}
          onChange={(e) => {
            setTexto(e.target.value);
            setActiva(null);
          }}
          onKeyDown={alTeclear}
        />
        {ocupado && <Giro />}
        <button
          type="button"
          className="buscador-tarea__cerrar"
          aria-label="Cerrar el buscador"
          title="Cerrar (Esc)"
          onClick={onCerrar}
        >
          <Icono nombre="x" tamano={14} />
        </button>
      </div>
      <p id={`${id}-estado`} className="buscador-tarea__estado" aria-live="polite">
        {estadoBusqueda}
      </p>
      <ul
        id={`${id}-lista`}
        className="buscador-tarea__lista"
        role="listbox"
        aria-label={etiqueta}
        hidden={opciones.length === 0}
      >
        {opciones.map((opcion) => {
          const { tarea, motivo } = opcion;
          return (
            <li
              key={tarea.id}
              id={idOpcion(tarea.id)}
              role="option"
              aria-selected={tarea.id === activaVigente}
              aria-disabled={motivo !== null || undefined}
              // El foco se queda en el campo: el clic no lo roba.
              onMouseDown={(e) => e.preventDefault()}
              onMouseMove={() => motivo === null && setActiva(tarea.id)}
              onClick={() => elegir(opcion)}
            >
              <span className="buscador-tarea__id">{formatearNumero(tarea.numero)}</span>
              <span className="buscador-tarea__titulo" title={tarea.titulo}>
                {tarea.titulo}
              </span>
              {motivo ? (
                <span className="buscador-tarea__motivo" title={motivo}>
                  {motivo}
                </span>
              ) : (
                <EstadoTarea estado={tarea.estado} />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
